import {
  ChevronRightOutlineIcon,
  ChevronDownOutlineIcon,
  SettingsOutlineIcon,
  CloseOutlineIcon,
  CheckMarkOutlineIcon,
} from '@applyboard/ui-icons';
import { ExpandedRow } from '@tanstack/react-table';
import { hasFlag } from 'country-flag-icons';
import * as Flags from 'country-flag-icons/react/3x2';
import { format, parseISO } from 'date-fns';
import { getCountries } from 'libphonenumber-js';
import React from 'react';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { Link } from '../Link';
import { MenuButton } from '../MenuButton';
import { SrOnly } from '../SrOnly';
import { Text, TextProps } from '../Text';
import { Tooltip } from '../Tooltip';
import { IconType, Intent, useFullTheme } from '~/design';
import { allowedTooltipSymbols } from '~/utils/tooltip';
import { IconWrapper } from '~/internal';

export interface CellProps {
  /** The content to render. */
  children: React.ReactNode;
}

export function Cell({ children }: CellProps) {
  const { colors, spacing } = useFullTheme();
  return <div css={{ backgroundColor: colors.bgRegular, padding: spacing[4] }}>{children}</div>;
}

export interface TextCellProps {
  /** Background color of the cell. Uses Intent values. */
  background?: Intent;

  children: TextProps['children'];

  /** Contrast color for text. Applied to `children` */
  contrast?: TextProps['contrast'];

  /** Text intent. Applied to `children` */
  intent?: TextProps['intent'];

  /** How the text overflows. Applied to `children` */
  overflow?: 'nowrap';

  /** The size/weight to use. Applied to `children` */
  variant?: TextProps['variant'];

  /** The wordBreak attribute value to use. Applied to `children` */
  wordBreak?: TextProps['wordBreak'];
}

export function TextCell({
  background,
  children,
  contrast,
  intent,
  overflow,
  variant = 'bodyM',
  wordBreak,
}: TextCellProps) {
  const { colors, spacing } = useFullTheme();
  return (
    <div
      css={{
        alignItems: 'center',
        backgroundColor: background
          ? {
              magic: colors.bgMagic,
              negative: colors.bgNegative,
              positive: colors.bgPositive,
              primary: colors.bgPrimary,
              secondary: colors.bgSecondary,
              warning: colors.bgWarning,
            }[background]
          : colors.bgRegular,
        display: 'flex',
        height: '100%',
        padding: spacing[4],
      }}
    >
      <Text
        contrast={contrast}
        intent={intent}
        overflow={overflow}
        variant={variant}
        wordBreak={wordBreak}
      >
        {children}
      </Text>
    </div>
  );
}

export interface ActionsCellProps {
  /** Children for MenuButton. */
  children: React.ReactNode;

  /** The icon to use for the MenuButton trigger. (default: SettingsOutlineIcon) */
  icon?: IconType;
}

export function ActionsCell({ children, icon }: ActionsCellProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Cell>
      <MenuButton open={open} onOpenChange={setOpen}>
        <MenuButton.Trigger>
          <Button
            aria-label="Open actions"
            emphasis="transparent"
            leadIcon={icon ?? SettingsOutlineIcon}
            onClick={() => setOpen(true)}
            size="sm"
          />
        </MenuButton.Trigger>
        {children}
      </MenuButton>
    </Cell>
  );
}

export interface BooleanCellProps {
  /** If true, a checkmark icon will be rendered. If false, an X icon will be rendered. There is
   * also screenreader-only text to accompany the icon. (default: false) */
  value?: boolean;
}

export function BooleanCell({ value }: BooleanCellProps) {
  const Icon = value ? CheckMarkOutlineIcon : CloseOutlineIcon;
  return (
    <Cell>
      <IconWrapper intent={value ? 'positive' : 'negative'} selector="div">
        <Icon />
      </IconWrapper>
      <SrOnly>{value ? 'Yes' : 'No'}</SrOnly>
    </Cell>
  );
}

export interface DateCellProps {
  /** The format to render the date in. See the date-fns documentation for possible formats.
   * (default: yyyy-MMM-dd) */
  format?: string;

  /** The date to display. */
  value?: string;
}

export function DateCell({ format: dateFormat, value }: DateCellProps) {
  return (
    <Cell>
      <Text overflow="nowrap">
        {value ? format(parseISO(value), dateFormat ?? 'yyyy-MMM-dd') : ''}
      </Text>
    </Cell>
  );
}

export interface EmailCellProps {
  /** The email to display. */
  value?: string;
}

export function EmailCell({ value }: EmailCellProps) {
  return (
    <Cell>
      {value ? (
        <div css={{ maxWidth: 220 }}>
          <Text overflow="ellipsis">
            <Tooltip label={value} placement="top-start">
              <Link href={`mailto:${value}`}>{value}</Link>
            </Tooltip>
          </Text>
        </div>
      ) : (
        ''
      )}
    </Cell>
  );
}

export interface ExpandCellProps {
  /** A function that determines if the row can be expanded. */
  getCanExpand: () => boolean;

  /** A function that returns whether or not the row is expanded. */
  getIsExpanded: () => boolean;

  /** A callback function that is invoked when the expand button is clicked. */
  getToggleExpandedHandler: ExpandedRow['getToggleExpandedHandler'];
}

export function ExpandCell({
  getCanExpand,
  getIsExpanded,
  getToggleExpandedHandler,
}: ExpandCellProps) {
  const isExpanded = getCanExpand() ? getIsExpanded() : false;
  return getCanExpand() ? (
    <Button
      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
      emphasis="transparent"
      leadIcon={isExpanded ? ChevronDownOutlineIcon : ChevronRightOutlineIcon}
      onClick={getToggleExpandedHandler()}
      size="sm"
    />
  ) : null;
}

const countryName = new Intl.DisplayNames(['en'], { type: 'region' });
const countryOptions = getCountries().map(country => {
  return {
    code: country,
    name: countryName.of(country) ?? country,
  };
});

export interface CountryCellProps {
  /** If true, the country's flag will be hidden. (default: false) */
  hideFlag?: boolean;

  /** The country to display. Can be a country name or its ISO code. */
  value?: string;
}

export function CountryCell({ hideFlag = false, value }: CountryCellProps) {
  const country = countryOptions.find(opt => opt.code === value || opt.name === value);
  const icon = !!country && hasFlag(country.code) ? Flags[country.code] : undefined;
  const Flag = icon ? (icon as Flags.FlagComponent) : null;
  const name = country?.name ?? value;

  if (!country) {
    return (
      <Cell>
        <Text variant="bodyS">{name}</Text>
      </Cell>
    );
  }

  return (
    <Cell>
      <Tooltip label={country?.name ?? ''}>
        <CountryButton code={country?.code} flag={hideFlag ? null : Flag} name={name} />
      </Tooltip>
    </Cell>
  );
}

interface CountryButtonProps {
  code?: string;
  flag: Flags.FlagComponent | null;
  name?: string;
}

const CountryButton = React.forwardRef<HTMLButtonElement, CountryButtonProps>(
  function CountryButton({ code, flag: Flag, name, ...props }, ref) {
    return (
      <button
        css={{
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
        {...props}
        ref={ref}
        type="button"
      >
        <Flex gap={1}>
          {Flag && <Flag width={24} />}
          <span aria-hidden>
            <Text variant="bodyS">{code}</Text>
          </span>

          <SrOnly>{name}</SrOnly>
        </Flex>
      </button>
    );
  },
);
Object.assign(CountryButton, { [allowedTooltipSymbols.CountryButton]: true });
